/**
 * @author: lv.chen
 * @date: 2025/12/09
 * @desc: CasualGamesHub/CasualTopChartsST - Casual Top Charts (SensorTower) 页面
 */
import React from 'react';
import classnames from 'classnames';
import { useIntl, history } from 'umi';
import ReuseTabPageTemplate from '@intelligence/v3/components/ReuseTabPageTemplate';
import ActiveUsers from '@intelligence/v3/pages/TopCharts/MobileGames/ActiveUsers';
import useUrlSyncActiveId from '@intelligence/v3/_hooks/useUrlSyncActiveId';

interface ICasualTopChartsSTProps extends IBaseProps {}

type TTabId = 'casual' | 'hyper' | 'iaa';

interface ITabConfig {
  id: TTabId;
  label: string;
  annotation: string;
  pageLogKey: string;
  downloadLogKey: string;
  condKey?: string;
}

const TAB_IDS: TTabId[] = ['casual', 'hyper', 'iaa'];
const DEFAULT_ACTIVE_TAB_ID: TTabId = 'casual';
const PAGE_PATH_PREFIX = '/intelligence/casualGamesHub/casualTopChartsST';

const CasualTopChartsST: React.FC<ICasualTopChartsSTProps> = (props) => {
  const { style, className } = props;

  const { formatMessage } = useIntl();
  const query = history.location.query || {};
  const [activeId, setActiveId] = useUrlSyncActiveId(TAB_IDS, DEFAULT_ACTIVE_TAB_ID);

  const tabCondKeysFromBackend = React.useMemo(() => {
    try {
      const encoded = query.tab_cond_keys as string;
      if (!encoded) {
        return {};
      }
      return JSON.parse(decodeURIComponent(encoded));
    } catch (error) {
      console.error('[Tab Cond Keys] Parse error:', error);
      return {};
    }
  }, [query.tab_cond_keys]);

  const tabConfigs = React.useMemo(() => {
    const configs: Record<TTabId, ITabConfig> = {
      casual: {
        id: 'casual',
        label: formatMessage({ id: 'dbi_tab_label_casual_hyper_hybrid' }),
        annotation: formatMessage({ id: 'dbi_tab_annotation_casual_st' }),
        pageLogKey: 'casualGamesHubCasualTopChartsSTCasual',
        downloadLogKey: 'casualGamesHubCasualTopChartsSTCasualDownloadClick',
      },
      hyper: {
        id: 'hyper',
        label: formatMessage({ id: 'dbi_tab_label_hypercasual' }),
        annotation: formatMessage({ id: 'dbi_tab_annotation_hyper_st' }),
        pageLogKey: 'casualGamesHubCasualTopChartsSTHyper',
        downloadLogKey: 'casualGamesHubCasualTopChartsSTHyperDownloadClick',
      },
      iaa: {
        id: 'iaa',
        label: formatMessage({ id: 'dbi_tab_label_iaa' }),
        annotation: formatMessage({ id: 'dbi_tab_annotation_iaa_st' }),
        pageLogKey: 'casualGamesHubCasualTopChartsSTIAA',
        downloadLogKey: 'casualGamesHubCasualTopChartsSTIAADownloadClick',
      },
    };

    Object.keys(configs).forEach((tabId) => {
      const backendCondKey = (tabCondKeysFromBackend as Record<string, string>)[tabId];
      if (backendCondKey) {
        configs[tabId as keyof typeof configs].condKey = backendCondKey;
      }
    });

    return configs;
  }, [formatMessage, tabCondKeysFromBackend]);

  /**
   * Handle tab change and update URL with filter_tag_name for all tabs
   * This triggers the cond API to fetch filter configuration
   */
  const handleTabChange = (tab: { id: string }): void => {
    const tabId = (tab.id as TTabId) || DEFAULT_ACTIVE_TAB_ID;
    if (tabId === activeId) {
      return;
    }

    setActiveId(tabId);

    const currentQuery = history.location.query || {};
    const condKey = tabConfigs[tabId]?.condKey;

    history.push({
      pathname: `${PAGE_PATH_PREFIX}/${tabId}`,
      query: {
        ...currentQuery,
        ...(condKey ? { filter_tag_name: condKey } : {}),
      },
    });
  };

  const tabs = React.useMemo(
    () =>
      Object.values(tabConfigs).map((config) => ({
        id: config.id,
        label: config.label,
        component: (
          <ActiveUsers
            pageLogConfigKey={config.pageLogKey}
            downloadLogConfigKey={config.downloadLogKey}
            disableInheritance={true}
          />
        ),
      })),
    [tabConfigs],
  );

  const activeTab = tabs.find((item) => item.id === activeId) || tabs[0];
  const activeConfig = tabConfigs[activeId as TTabId];

  return (
    <ReuseTabPageTemplate
      style={style}
      className={classnames('dbi-reuse-page-casual-top-charts-st', className)}
      title={formatMessage({ id: 'dbi_reuse_page_title_casual_top_charts_st' })}
      subTitle={activeConfig?.annotation}
      pageLogKeyMap={{
        casual: tabConfigs.casual.pageLogKey,
        hyper: tabConfigs.hyper.pageLogKey,
        iaa: tabConfigs.iaa.pageLogKey,
      }}
      tabs={tabs}
      activeId={activeId}
      onActive={handleTabChange}
    >
      <div key={activeId}>{activeTab?.component}</div>
    </ReuseTabPageTemplate>
  );
};

export default CasualTopChartsST;
